import React from "react"

import { useBillingSubscription } from "@/billing"
import {
  DEFAULT_MODEL,
  DEFAULT_TEXT_JUDGE_MODEL,
} from "@/metrics/registry/configuration/values"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui/select"
import { Tooltip } from "@coval/ui/tooltip"
import { Info, Lock } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../styles"
import { AI_MODELS, type AIModelSurface } from "../utils/aiModelOptions"
import { RequiredMarker } from "./RequiredMarker"
import { ThinkingConfig } from "./ThinkingConfig"

interface LLMModelSelectProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
  defaultModel?: string
  supportsCovalJudge?: boolean
  modelSurface?: AIModelSurface
}

export const LLMModelSelect: React.FC<LLMModelSelectProps> = ({
  metricRecordData,
  onChange,
  defaultModel = DEFAULT_MODEL,
  supportsCovalJudge = false,
  modelSurface = "regular_text_judge",
}) => {
  const { data: subscription } = useBillingSubscription()
  const isEnterprise = subscription?.tier === "enterprise"
  const storedModel = metricRecordData.metric_metadata?.model_version
  const llmModel = supportsCovalJudge
    ? storedModel
    : storedModel || defaultModel
  const isModelMissing = supportsCovalJudge && !llmModel
  const knowledgeBaseEnabled =
    metricRecordData.metric_metadata?.use_knowledge_base || false
  const modelsForSurface = AI_MODELS.filter(
    (model) =>
      model.supportedSurfaces === undefined ||
      model.supportedSurfaces.includes(modelSurface),
  )
  const availableModels = supportsCovalJudge
    ? modelsForSurface
    : modelsForSurface.filter((model) => model.id !== DEFAULT_TEXT_JUDGE_MODEL)

  const selectedModel = availableModels.find((model) => model.id === llmModel)

  const handleModelChange = (newModelId: string) => {
    const newModel = availableModels.find((model) => model.id === newModelId)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        model_version: newModelId,
        // Clear thinking when switching to a model that doesn't support it
        model_thinking_enabled: newModel?.supportsThinking
          ? metricRecordData.metric_metadata?.model_thinking_enabled
          : undefined,
      },
    })
  }

  const handleThinkingChange = (enabled: boolean) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        model_thinking_enabled: enabled,
      },
    })
  }

  return (
    <div className={FIELD_CONTAINER_STYLES}>
      <div className={LABEL_ROW_STYLES}>
        <label className={LABEL_STYLES}>
          Model
          {supportsCovalJudge && <RequiredMarker />}
        </label>
        <Tooltip
          content={
            knowledgeBaseEnabled && !isModelMissing
              ? "Model selection is disabled when Knowledge Base is enabled"
              : "Choose the LLM for metric evaluation"
          }
        >
          <button type="button" className="inline-flex" aria-label="Info">
            <Info className={INFO_ICON_STYLES} />
          </button>
        </Tooltip>
      </div>
      <Select
        value={llmModel ?? ""}
        onValueChange={handleModelChange}
        disabled={knowledgeBaseEnabled && !isModelMissing}
      >
        <SelectTrigger aria-label="Model" aria-invalid={isModelMissing}>
          <SelectValue placeholder="Select a model" />
        </SelectTrigger>
        <SelectContent>
          {availableModels.map((model) => {
            const isLocked = model.tier === "enterprise" && !isEnterprise
            return (
              <SelectItem key={model.id} value={model.id} disabled={isLocked}>
                <div className="flex items-center justify-between gap-2">
                  <span>{model.label}</span>
                  {isLocked && (
                    // Right: a select row's tooltip opening upward would cover the rows above it.
                    <Tooltip content="Enterprise plan required" side="right">
                      <button
                        type="button"
                        className="inline-flex"
                        aria-label="Enterprise plan required"
                      >
                        <Lock className="h-3 w-3 shrink-0 text-coval-text-muted" />
                      </button>
                    </Tooltip>
                  )}
                  {model.tier === "enterprise" && isEnterprise && (
                    <span className="rounded-full bg-coval-warning-surface px-1.5 py-0.5 text-[10px] font-medium text-coval-warning-on-surface">
                      Enterprise
                    </span>
                  )}
                </div>
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
      {isModelMissing && (
        <p className="mt-1 text-xs text-coval-danger-text" role="alert">
          Model required. Select a model to continue.
        </p>
      )}
      {selectedModel?.tier === "enterprise" && !isEnterprise && (
        <p className="mt-1 text-xs text-coval-warning-text">
          Enterprise plan required for this model.
        </p>
      )}
      {selectedModel?.supportsThinking && !knowledgeBaseEnabled && (
        <ThinkingConfig
          enabled={
            metricRecordData.metric_metadata?.model_thinking_enabled ?? false
          }
          onChange={handleThinkingChange}
        />
      )}
    </div>
  )
}
